import React, { useCallback, useEffect, useState } from 'react';
import AnalyticsRoundedIcon from '@mui/icons-material/AnalyticsRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Alert,
  Box,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  Paper,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import { fetchPatternPerformanceBreakdown } from '../../api/adminPatternPerformance';
import {
  BUCKET_TIMEFRAMES,
  DEFAULT_BUCKET_ENGINE_VERSIONS,
  DEFAULT_BUCKET_TIMEFRAMES,
  HISTORICAL_PATTERN_ENGINE_VERSIONS,
  resolvePatternEngineVersionLabel,
} from '../pattern-scanner/scannerExperienceShared';

const PAGE_COLORS = {
  ink: '#17323c',
  muted: '#4f646f',
  success: '#2f7d5d',
  warning: '#b57422',
  danger: '#9c4f4f',
};

const SCORE_BUCKETS = ['<50', '50-59', '60-69', '70-79', '80-89', '90-100'];

const POP_DETECTIONS = 'détections non ambiguës';
const POP_DEFINITIVE = 'évaluations TP définitives (RESOLVED + INVALIDATED)';

/**
 * Metric catalogue (short label + full name + definition + population). Values are attached per row in
 * `buildMetrics`. BO/Inv. use the detection population; the four TP metrics use the definitive
 * evaluation population (PENDING/UNKNOWN/AMBIGUOUS excluded).
 */
const METRIC_DEFS = [
  {
    key: 'breakout',
    short: 'BO',
    full: 'Breakout',
    population: POP_DETECTIONS,
    definition: 'Part des détections non ambiguës ayant cassé le niveau de breakout.',
    color: PAGE_COLORS.ink,
  },
  {
    key: 'invalidation',
    short: 'Inv.',
    full: 'Invalidation du pattern',
    population: POP_DETECTIONS,
    definition: "Part des détections invalidées parmi les détections non ambiguës. Une invalidation peut survenir après l'atteinte d'un ou plusieurs objectifs TP.",
    color: PAGE_COLORS.danger,
  },
  {
    key: 'invalidationBeforeTp1',
    short: 'Échec TP1',
    full: 'Invalidation avant TP1',
    population: POP_DEFINITIVE,
    definition: "Proportion d'évaluations définitives invalidées avant l'atteinte du premier objectif (échec avant TP1).",
    color: PAGE_COLORS.danger,
  },
  {
    key: 'tp1',
    short: 'TP1',
    full: 'TP1 atteint',
    population: POP_DEFINITIVE,
    definition: 'Part des évaluations définitives ayant atteint le premier objectif.',
    color: PAGE_COLORS.success,
  },
  {
    key: 'tp2',
    short: 'TP2',
    full: 'TP2 atteint',
    population: POP_DEFINITIVE,
    definition: 'Part des évaluations définitives ayant atteint le deuxième objectif.',
    color: PAGE_COLORS.success,
  },
  {
    key: 'tp3',
    short: 'TP3',
    full: 'TP3 atteint',
    population: POP_DEFINITIVE,
    definition: 'Part des évaluations définitives ayant atteint le troisième objectif.',
    color: PAGE_COLORS.success,
  },
];

function toNumber(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function normalizeRatio(value) {
  const numeric = toNumber(value);
  if (numeric === null) {
    return null;
  }
  return Math.abs(numeric) > 1.5 ? numeric / 100 : numeric;
}

function formatPercent(value, fallback = '—') {
  const normalized = normalizeRatio(value);
  if (normalized === null) {
    return fallback;
  }
  return `${(normalized * 100).toFixed(1)}%`;
}

function toCount(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
}

function compareEngineVersions(left, right) {
  if (left === right) {
    return 0;
  }
  const leftNumber = Number(left);
  const rightNumber = Number(right);
  if (Number.isFinite(leftNumber) && Number.isFinite(rightNumber)) {
    return leftNumber - rightNumber;
  }
  return String(left).localeCompare(String(right));
}

function versionLabel(version) {
  if (version === null || version === undefined || version === '') {
    return '';
  }
  return resolvePatternEngineVersionLabel(version).toUpperCase();
}

function LowSampleChip({ lowSample, threshold }) {
  if (!lowSample) {
    return null;
  }
  return (
    <Chip
      size="small"
      label={`Echantillon < ${threshold}`}
      sx={{
        height: 20,
        bgcolor: alpha(PAGE_COLORS.warning, 0.12),
        color: PAGE_COLORS.warning,
        fontWeight: 700,
      }}
    />
  );
}

function ProvisionalMarker() {
  return (
    <Tooltip title="Échantillon partiel : certains taux (TP1/TP2/TP3, échec TP1) sont provisoires tant que le backfill historique n'est pas complet.">
      <Box
        component="span"
        tabIndex={0}
        aria-label="Taux provisoires (échantillon partiel)"
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: PAGE_COLORS.warning,
          display: 'inline-block',
          flex: '0 0 auto',
        }}
      />
    </Tooltip>
  );
}

function isProvisional(takeProfits) {
  return Boolean(takeProfits)
    && takeProfits.coverageComplete !== true
    && toCount(takeProfits.definitiveCount) > 0;
}

function coverageLabel(takeProfits, detections) {
  return `TP définitifs : ${toCount(takeProfits?.definitiveCount)} / ${toCount(detections)}`;
}

function buildMetrics(row) {
  const tp = row.takeProfits;
  const definitive = tp ? toCount(tp.definitiveCount) : 0;
  const value = (rate, numerator, denominator) => ({
    rate,
    numerator,
    denominator,
    text: rate !== null && rate !== undefined ? formatPercent(rate) : '—',
  });
  const map = {
    breakout: value(toNumber(row.breakoutRate), toNumber(row.breakoutCount), null),
    invalidation: value(toNumber(row.invalidationRate), toNumber(row.invalidationCount), null),
    invalidationBeforeTp1: value(tp?.invalidationBeforeTp1Rate, tp?.invalidationBeforeTp1Count, definitive),
    tp1: value(tp?.tp1Rate, tp?.tp1Count, definitive),
    tp2: value(tp?.tp2Rate, tp?.tp2Count, definitive),
    tp3: value(tp?.tp3Rate, tp?.tp3Count, definitive),
  };
  return METRIC_DEFS.map((def) => {
    const metricValue = map[def.key];
    const hasDenominator = metricValue.denominator !== null && metricValue.denominator !== undefined;
    return {
      ...def,
      ...metricValue,
      available: metricValue.rate !== null && metricValue.rate !== undefined
        && (!hasDenominator || toCount(metricValue.denominator) > 0),
      fraction: hasDenominator && toCount(metricValue.denominator) > 0
        ? `${toCount(metricValue.numerator)} / ${toCount(metricValue.denominator)} évalués`
        : (metricValue.numerator !== null && metricValue.numerator !== undefined
          ? `${toCount(metricValue.numerator)} détection(s)` : null),
    };
  });
}

function MetricTooltip({ metric }) {
  return (
    <Box sx={{ maxWidth: 300 }}>
      <Typography variant="body2" sx={{ fontWeight: 800 }}>{metric.full}</Typography>
      <Typography variant="caption" sx={{ display: 'block' }}>{`Population : ${metric.population}.`}</Typography>
      {metric.definition ? (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>{metric.definition}</Typography>
      ) : null}
      {metric.fraction ? (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontWeight: 700 }}>{metric.fraction}</Typography>
      ) : null}
    </Box>
  );
}

function MetricValue({ metric }) {
  return (
    <Tooltip title={<MetricTooltip metric={metric} />} enterTouchDelay={0} leaveTouchDelay={6000}>
      <Box
        component="span"
        tabIndex={0}
        sx={{
          cursor: 'help',
          fontWeight: 700,
          fontSize: 14,
          whiteSpace: 'nowrap',
          color: metric.available ? metric.color : PAGE_COLORS.muted,
          outline: 'none',
          '&:focus-visible': { outline: `2px solid ${alpha(PAGE_COLORS.ink, 0.5)}`, borderRadius: 0.5 },
        }}
      >
        {metric.available ? metric.text : '—'}
      </Box>
    </Tooltip>
  );
}

function PatternCell({ row, showPattern, showVersion, provisional, threshold }) {
  return (
    <Box>
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
        {showPattern ? (
          <Typography
            variant="body2"
            title={row.pattern}
            sx={{ fontWeight: 700, color: PAGE_COLORS.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {row.pattern}
          </Typography>
        ) : null}
        {showVersion ? (
          <Chip
            size="small"
            label={versionLabel(row.engineVersion)}
            sx={{ height: 20, bgcolor: alpha(PAGE_COLORS.ink, 0.06), color: PAGE_COLORS.muted, fontWeight: 800 }}
          />
        ) : null}
        {provisional ? <ProvisionalMarker /> : null}
      </Stack>
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.25 }}>
        <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, lineHeight: 1.1 }}>
          {coverageLabel(row.takeProfits, row.detections)}
        </Typography>
        <LowSampleChip lowSample={row.lowSample} threshold={threshold} />
      </Stack>
    </Box>
  );
}

/** Accessible details view: every metric with its exact fraction/denominator and its definition. */
function RowDetailsContent({ row }) {
  const metrics = buildMetrics(row);
  return (
    <Box sx={{ p: 2, maxWidth: 420 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: PAGE_COLORS.ink }}>
          {row.pattern || 'Pattern'}
        </Typography>
        <Chip size="small" label={versionLabel(row.engineVersion)} sx={{ height: 20 }} />
      </Stack>
      <Typography variant="caption" sx={{ color: PAGE_COLORS.muted }}>
        {`${coverageLabel(row.takeProfits, row.detections)} · Détections : ${toCount(row.detections)}`}
      </Typography>
      <Divider sx={{ my: 1 }} />
      <Stack spacing={0.75}>
        {metrics.map((metric) => (
          <Stack key={metric.key} direction="row" justifyContent="space-between" spacing={2} alignItems="baseline">
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{metric.full}</Typography>
              <Typography variant="caption" sx={{ color: PAGE_COLORS.muted }}>{metric.population}</Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body2" sx={{ fontWeight: 800, color: metric.available ? metric.color : PAGE_COLORS.muted }}>
                {metric.available ? metric.text : '—'}
              </Typography>
              <Typography variant="caption" sx={{ color: PAGE_COLORS.muted }}>{metric.fraction || '—'}</Typography>
            </Box>
          </Stack>
        ))}
      </Stack>
      <Typography variant="caption" sx={{ display: 'block', mt: 1.25, color: PAGE_COLORS.muted }}>
        BO / Inv. : détections non ambiguës. Échec TP1 / TP1 / TP2 / TP3 : évaluations TP définitives.
        Les évaluations PENDING n&apos;entrent pas dans le dénominateur définitif.
      </Typography>
    </Box>
  );
}

function ConfigurablePatternPerformanceTable({ appliedFilters = {}, lowSampleThreshold = 30 }) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [showPattern, setShowPattern] = useState(true);
  const [selectedVersions, setSelectedVersions] = useState(DEFAULT_BUCKET_ENGINE_VERSIONS);
  const [selectedTimeframes, setSelectedTimeframes] = useState(DEFAULT_BUCKET_TIMEFRAMES);
  const [selectedScoreBuckets, setSelectedScoreBuckets] = useState([]);
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [details, setDetails] = useState({ anchorEl: null, row: null });

  const orderedVersions = [...HISTORICAL_PATTERN_ENGINE_VERSIONS].sort(compareEngineVersions);

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const requestFilters = { ...appliedFilters };
      // Les sélections locales sont l'autorité pour ce breakdown : le filtre
      // global ne doit pas les neutraliser.
      delete requestFilters.version;
      delete requestFilters.timeframe;
      const payload = await fetchPatternPerformanceBreakdown({
        ...requestFilters,
        groupByPattern: showPattern,
        engineVersions: selectedVersions.join(','),
        timeframes: selectedTimeframes.join(','),
        scoreBuckets: selectedScoreBuckets.join(','),
      });
      setRows(payload?.rows || []);
    } catch (loadError) {
      setRows(null);
      setError(loadError.message || 'Impossible de charger la segmentation par bucket de score.');
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, showPattern, selectedVersions, selectedTimeframes, selectedScoreBuckets]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const toggleVersion = (version) => (event) => {
    const checked = event.target.checked;
    setSelectedVersions((current) => (
      checked ? [...current, version] : current.filter((item) => item !== version)
    ));
  };

  const toggleTimeframe = (timeframe) => (event) => {
    const checked = event.target.checked;
    setSelectedTimeframes((current) => (
      checked ? [...current, timeframe] : current.filter((item) => item !== timeframe)
    ));
  };

  const toggleScoreBucket = (bucket) => (event) => {
    const checked = event.target.checked;
    setSelectedScoreBuckets((current) => (
      checked ? [...current, bucket] : current.filter((item) => item !== bucket)
    ));
  };

  const showVersionColumn = selectedVersions.length > 0;
  const openDetails = (event, row) => setDetails({ anchorEl: event.currentTarget, row });
  const closeDetails = () => setDetails({ anchorEl: null, row: null });

  const renderDesktopTable = (list) => (
    <Table size="small" sx={{ tableLayout: 'auto' }}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 800, whiteSpace: 'nowrap', py: 1 }}>
            {showPattern ? 'Pattern' : 'Version'}
          </TableCell>
          {METRIC_DEFS.map((def) => (
            <TableCell key={def.key} align="center" sx={{ fontWeight: 800, whiteSpace: 'nowrap', px: 0.5, py: 1 }}>
              <Tooltip title={<MetricTooltip metric={def} />}><span>{def.short}</span></Tooltip>
            </TableCell>
          ))}
          <TableCell align="center" sx={{ width: 1, px: 0.25 }} />
        </TableRow>
      </TableHead>
      <TableBody>
        {list.map((row, index) => {
          const metrics = buildMetrics(row);
          const provisional = isProvisional(row.takeProfits);
          return (
            <TableRow
              key={`${row.pattern || ''}-${row.timeframe || ''}-${row.engineVersion || ''}-${index}`}
              hover
            >
              <TableCell sx={{ maxWidth: 260, py: 0.75 }}>
                  <PatternCell row={row} showPattern={showPattern} showVersion={showVersionColumn} provisional={provisional} threshold={lowSampleThreshold} />
              </TableCell>
              {metrics.map((metric) => (
                <TableCell key={metric.key} align="center" sx={{ px: 0.5, py: 0.75 }}>
                  <MetricValue metric={metric} />
                </TableCell>
              ))}
              <TableCell align="center" sx={{ px: 0.25, py: 0.5 }}>
                <Tooltip title={<span>Détails : taux, fractions et dénominateurs</span>}>
                  <IconButton
                    size="small"
                    aria-label={`Détails statistiques ${row.pattern || ''} ${row.engineVersion || ''}`.trim()}
                    onClick={(event) => openDetails(event, row)}
                  >
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );

  const renderMobileCards = (list) => (
    <Stack spacing={1.25}>
      {list.map((row, index) => {
        const metrics = buildMetrics(row);
        const provisional = isProvisional(row.takeProfits);
        return (
          <Card
            key={`${row.pattern || ''}-${row.timeframe || ''}-${row.engineVersion || ''}-${index}`}
            variant="outlined"
            sx={{ borderRadius: 3 }}
          >
            <Box sx={{ p: 1.25 }}>
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                <Box sx={{ minWidth: 0 }}>
                <PatternCell row={row} showPattern={showPattern} showVersion={showVersionColumn} provisional={provisional} threshold={lowSampleThreshold} />
                </Box>
                <IconButton
                  size="small"
                  aria-label={`Détails statistiques ${row.pattern || ''} ${row.engineVersion || ''}`.trim()}
                  onClick={(event) => openDetails(event, row)}
                >
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Stack>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                  gap: 1,
                  mt: 1.25,
                }}
              >
                {metrics.map((metric) => (
                  <Box key={metric.key} sx={{ minWidth: 0 }}>
                    <Tooltip title={<MetricTooltip metric={metric} />} enterTouchDelay={0} leaveTouchDelay={6000}>
                      <Box component="span" tabIndex={0} sx={{ cursor: 'help', outline: 'none' }}>
                        <Typography variant="caption" sx={{ display: 'block', color: PAGE_COLORS.muted, fontWeight: 800 }}>
                          {metric.short}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 800, color: metric.available ? metric.color : PAGE_COLORS.muted }}
                        >
                          {metric.available ? metric.text : '—'}
                        </Typography>
                      </Box>
                    </Tooltip>
                  </Box>
                ))}
              </Box>
            </Box>
          </Card>
        );
      })}
    </Stack>
  );

  const renderBody = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} sx={{ color: PAGE_COLORS.ink }} />
        </Box>
      );
    }
    if (error) {
      return <Alert severity="error">{error}</Alert>;
    }
    if (!rows?.length) {
      return <Alert severity="info">Aucune donnée pour cette sélection.</Alert>;
    }

    return (
      <Box>
        {isDesktop
          ? <TableContainer>{renderDesktopTable(rows)}</TableContainer>
          : renderMobileCards(rows)}
        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: PAGE_COLORS.muted }}>
          BO et Inv. (invalidation du pattern) sont calculés sur les détections non ambiguës. Échec TP1,
          TP1, TP2 et TP3 sont calculés sur les évaluations TP définitives ; les évaluations PENDING,
          UNKNOWN et AMBIGUOUS en sont exclues. Les taux peuvent être provisoires pendant le backfill
          historique.
        </Typography>
      </Box>
    );
  };

  return (
    <Paper sx={{ p: 3, borderRadius: 5, background: '#fff', border: `1px solid ${alpha(PAGE_COLORS.ink, 0.08)}` }}>
      <Stack spacing={2.1}>
        <Stack direction="row" spacing={1.1} alignItems="center">
          <AnalyticsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: PAGE_COLORS.ink }}>
              Analyse de performance par Score
            </Typography>
            <Typography variant="body2" sx={{ color: PAGE_COLORS.muted, mt: 0.35 }}>
              Segmentation dynamique par pattern et version, croisée avec le bucket de score, filtrable par moteur, timeframe et score.
            </Typography>
          </Box>
        </Stack>

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          sx={{
            p: 1.5,
            borderRadius: 3,
            bgcolor: alpha(PAGE_COLORS.ink, 0.03),
            alignItems: { xs: 'flex-start', md: 'center' },
          }}
        >
          <Stack spacing={0.25}>
            <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, fontWeight: 800, letterSpacing: 0.6 }}>
              Dimensions
            </Typography>
            <FormGroup row>
              <FormControlLabel
                control={<Checkbox size="small" checked={showPattern} onChange={(event) => setShowPattern(event.target.checked)} />}
                label="Pattern"
              />
            </FormGroup>
          </Stack>

          <Stack spacing={0.25}>
            <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, fontWeight: 800, letterSpacing: 0.6 }}>
              Versions moteur
            </Typography>
            <FormGroup row>
              {orderedVersions.map((version) => (
                <FormControlLabel
                  key={version}
                  control={(
                    <Checkbox
                      size="small"
                      checked={selectedVersions.includes(version)}
                      onChange={toggleVersion(version)}
                    />
                  )}
                  label={versionLabel(version)}
                />
              ))}
            </FormGroup>
          </Stack>

          <Stack spacing={0.25}>
            <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, fontWeight: 800, letterSpacing: 0.6 }}>
              Timeframes
            </Typography>
            <FormGroup row>
              {BUCKET_TIMEFRAMES.map((option) => (
                <FormControlLabel
                  key={option.value}
                  control={(
                    <Checkbox
                      size="small"
                      checked={selectedTimeframes.includes(option.value)}
                      onChange={toggleTimeframe(option.value)}
                    />
                  )}
                  label={option.label}
                />
              ))}
            </FormGroup>
          </Stack>

          <Stack spacing={0.25}>
            <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, fontWeight: 800, letterSpacing: 0.6 }}>
              Scores
            </Typography>
            <FormGroup row>
              {SCORE_BUCKETS.map((bucket) => (
                <FormControlLabel
                  key={bucket}
                  control={(
                    <Checkbox
                      size="small"
                      checked={selectedScoreBuckets.includes(bucket)}
                      onChange={toggleScoreBucket(bucket)}
                    />
                  )}
                  label={bucket}
                />
              ))}
            </FormGroup>
          </Stack>
        </Stack>

        {renderBody()}

        <Popover
          open={Boolean(details.anchorEl)}
          anchorEl={details.anchorEl}
          onClose={closeDetails}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          {details.row ? <RowDetailsContent row={details.row} /> : null}
        </Popover>
      </Stack>
    </Paper>
  );
}

export default ConfigurablePatternPerformanceTable;
